import React from 'react';
import {
  Box,
  Center,
  Container,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MotionBox, variants } from '../components/motion';

export default function Terms() {
  return (
    <Center minH="100vh" bg="gray.50" px={4} py={8}>
      <MotionBox
        maxW="4xl"
        w="100%"
        p={8}
        borderWidth="1px"
        borderRadius="lg"
        bg="white"
        boxShadow="md"
        variants={variants.item}
        initial="hidden"
        animate="show"
      >
        <VStack spacing={6} align="stretch">
          <Box textAlign="center" borderBottom="1px" borderColor="gray.200" pb={4}>
            <Heading size="xl" mb={2}>Terms &amp; Conditions</Heading>
            <Text color="gray.600" fontSize="sm">
              Effective: September 10, 2025 · Last updated: September 10, 2025
            </Text>
          </Box>

          <Container maxW="container.lg" px={0}>
            <VStack spacing={5} align="stretch">
              <Box>
                <Heading size="md" mb={3}>1. Introduction</Heading>
                <Text color="gray.700">
                  These Terms &amp; Conditions ("Terms") govern your access to and use of the SmartSend platform and related services ("Services"). By selecting I Agree, creating an account, or using the Services, you accept these Terms.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>2. Definitions</Heading>
                <VStack align="stretch" spacing={2}>
                  <Text color="gray.700">
                    <strong>"We", "Us", "Our"</strong> means SmartSend and its affiliates.
                  </Text>
                  <Text color="gray.700">
                    <strong>"You", "Customer"</strong> means the individual or entity using the Services.
                  </Text>
                  <Text color="gray.700">
                    <strong>"Content"</strong> means messages, data, files, or other materials submitted to or through the Services.
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Heading size="md" mb={3}>3. Eligibility &amp; Account</Heading>
                <Text color="gray.700">
                  You must be of legal age and have authority to enter into these Terms. You agree to provide accurate registration information and to keep credentials confidential. You are responsible for all activities that occur under your account.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>4. Acceptable Use</Heading>
                <Text color="gray.700" mb={2}>
                  You agree not to misuse the Services. Prohibited actions include (without limitation):
                </Text>
                <VStack align="stretch" spacing={1} pl={4}>
                  <Text color="gray.700">• Violating any law or regulation (e.g., anti-spam, consumer protection, privacy, export).</Text>
                  <Text color="gray.700">• Sending unlawful, harmful, deceptive, or infringing Content.</Text>
                  <Text color="gray.700">• Interfering with or disrupting the integrity or performance of the Services.</Text>
                  <Text color="gray.700">• Attempting to gain unauthorized access to systems or data.</Text>
                  <Text color="gray.700">• Reverse engineering or creating derivative works from the Services where restricted by law.</Text>
                </VStack>
              </Box>

              <Box>
                <Heading size="md" mb={3}>5. Messaging &amp; Consent</Heading>
                <Text color="gray.700">
                  You are solely responsible for obtaining and documenting valid consent from message recipients and for honoring opt-out requests. You will comply with applicable carrier policies and regulations (e.g., opt-in/opt-out, content restrictions, quiet hours).
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>6. Privacy &amp; Data</Heading>
                <Text color="gray.700">
                  Our processing of personal data is described in our Privacy Policy. You remain the controller of personal data that you upload; we act as a processor to provide the Services. You must provide all required notices and obtain all necessary consents from data subjects.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>7. Fees &amp; Payment</Heading>
                <Text color="gray.700">
                  Fees, message rates, taxes, and billing cycles are displayed at purchase or in an order form. You authorize us and our payment processors to charge your payment method for all due amounts. Late payments may result in suspension.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>8. Service Availability</Heading>
                <Text color="gray.700">
                  We strive for high availability but do not guarantee uninterrupted service. Maintenance windows and carrier dependencies may impact delivery or performance.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>9. Intellectual Property</Heading>
                <Text color="gray.700">
                  We and our licensors own all rights in the Services and underlying technology. Except as expressly granted, no rights are transferred. You retain ownership of your Content.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>10. Feedback</Heading>
                <Text color="gray.700">
                  If you provide suggestions or feedback, you grant us a worldwide, royalty-free, irrevocable license to use, reproduce, and incorporate that feedback without restriction.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>11. Beta Features</Heading>
                <Text color="gray.700">
                  Features labeled "Beta", "Preview", or similar are provided "as is", may change at any time, and may be discontinued without notice.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>12. Term &amp; Termination</Heading>
                <Text color="gray.700">
                  These Terms apply from your first use until terminated. You may stop using the Services at any time. We may suspend or terminate access for violations of these Terms, legal requirements, or to prevent harm.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>13. Disclaimers</Heading>
                <Text color="gray.700" fontWeight="semibold">
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>14. Limitation of Liability</Heading>
                <Text color="gray.700" fontWeight="semibold">
                  TO THE FULLEST EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, OR LOST PROFITS, REVENUE, DATA, OR GOODWILL. OUR AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICES WILL NOT EXCEED THE AMOUNTS PAID BY YOU TO US FOR THE SERVICES IN THE 12 MONTHS PRECEDING THE CLAIM.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>15. Indemnification</Heading>
                <Text color="gray.700">
                  You will defend, indemnify, and hold harmless SmartSend and its affiliates from claims, damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from your Content, your use of the Services, or your violation of these Terms or applicable law.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>16. Governing Law &amp; Disputes</Heading>
                <Text color="gray.700">
                  These Terms are governed by the laws of our contracting entity's jurisdiction (without regard to conflict of laws). Disputes will be resolved in the courts of that jurisdiction, unless mandatory local law states otherwise.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>17. Changes to Terms</Heading>
                <Text color="gray.700">
                  We may modify these Terms by posting an updated version with a new "Last updated" date. Material changes will be communicated via the Service or email. Continued use after changes constitutes acceptance.
                </Text>
              </Box>

              <Box>
                <Heading size="md" mb={3}>18. Contact</Heading>
                <Text color="gray.700">
                  Questions? Email legal@smartsend.example.
                </Text>
              </Box>

              <Box pt={4} borderTop="1px" borderColor="gray.200">
                <Text color="gray.500" fontSize="sm" fontStyle="italic" textAlign="center">
                  This sample text is for implementation and testing only and does not constitute legal advice.
                </Text>
              </Box>
            </VStack>
          </Container>
        </VStack>
      </MotionBox>
    </Center>
  );
}
